<template>
  <Teleport to="body">
    <div class="howto-backdrop" @click.self="close">
      <div class="howto" role="dialog" aria-label="How to play">
        <button class="howto__close" aria-label="Close" @click="close">✕</button>

        <div class="howto__body">
          <h3>{{ slide.title }}</h3>

          <!-- A miniature of the real thing: five codenames, and on the key
               slide the colours a spymaster sees. -->
          <div v-if="slide.mini" class="howto__mini">
            <span
              v-for="cell in slide.mini"
              :key="cell.word"
              class="howto__mini-card"
              :class="cell.cls"
            >{{ cell.word }}</span>
          </div>

          <!-- eslint-disable-next-line vue/no-v-html -- content is this file's own
               constant strings below, never user input -->
          <p v-for="(line, i) in slide.lines" :key="i" class="howto__line" v-html="line" />
        </div>

        <div class="howto__nav">
          <button class="secondary" :disabled="index === 0" @click="index -= 1">Back</button>
          <span class="howto__dots">
            <i
              v-for="(s, i) in slides"
              :key="i"
              class="howto__dot"
              :class="{ 'howto__dot--on': i === index }"
            />
          </span>
          <button v-if="index < slides.length - 1" class="primary howto__next" @click="index += 1">
            Next
          </button>
          <button v-else class="primary howto__next" @click="close">
            Got it
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
// The walkthrough - the house pattern (auto-opens once, behind ? after).
// The example clue is the rulebook's own (TREE: 2 for NUT and BARK, p4);
// howToPlay.test.js asserts the claims against the engine.
const ROW = [
  { word: 'NUT', cls: '' }, { word: 'BARK', cls: '' }, { word: 'SPRING', cls: '' },
  { word: 'OPERA', cls: '' }, { word: 'MOLE', cls: '' },
];
const KEYED = [
  { word: 'NUT', cls: 'howto__mini-card--red' }, { word: 'BARK', cls: 'howto__mini-card--red' },
  { word: 'SPRING', cls: 'howto__mini-card--blue' }, { word: 'OPERA', cls: 'howto__mini-card--tan' },
  { word: 'MOLE', cls: 'howto__mini-card--assassin' },
];

const SLIDES = [
  {
    title: 'Two teams of spies',
    mini: ROW,
    lines: [
      '25 codenames on the table. Each one hides a <strong>red agent</strong>, a <strong>blue agent</strong>, an innocent bystander — or the <strong>assassin</strong>.',
      'Each team has a <strong>spymaster</strong>, who secretly knows which is which, and <strong>field operatives</strong>, who see only the words.',
      'First team to contact all their own agents wins.',
    ],
  },
  {
    title: 'One word, one number',
    mini: KEYED,
    lines: [
      'The spymaster gives a clue like <strong>TREE: 2</strong> — one word about meaning, and how many codenames it points at. Here it means NUT and BARK.',
      'The clue can\'t be a word on the table, and no extra hints — a straight face is part of the job.',
      'Operatives get the number of guesses <strong>plus one extra</strong>, and must always guess at least once.',
    ],
  },
  {
    title: 'Making contact',
    lines: [
      'Touch <strong>your own agent</strong>: keep guessing. Touch a <strong>bystander</strong> or the <strong>other team\'s agent</strong>: your turn ends (and the second one helps them).',
      'Touch the <strong>assassin</strong>: your team loses on the spot. Spymasters, make sure your clue steers well clear of it.',
      'You can stop guessing whenever you like — banking two safe contacts often beats fishing for a third.',
    ],
  },
  {
    title: 'Filling the table',
    lines: [
      'Each team needs a spymaster and at least one operative — four people for the classic game, and more operatives just make the arguments better.',
      'This is the one game on the hub with <strong>no robots</strong>, on purpose: the whole game is reading the person giving the clue.',
      'Expert corner: a clue number of <strong>0</strong> or <strong>∞</strong> lets your team guess as many as they dare.',
    ],
  },
];

export default {
  name: 'HowToPlay',
  emits: ['close'],
  data() {
    return { index: 0, slides: SLIDES };
  },
  computed: {
    slide() { return this.slides[this.index]; },
  },
  methods: {
    close() { this.$emit('close'); },
  },
};
</script>
